import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "@/components/Helmet";
import { Button } from "@/components/ui/button";
import { openAnalyticsPreferences } from "@/lib/analyticsConsent";
import { ChevronUp } from "lucide-react";

const LAST_UPDATED = "2026-04-09";

const sections = [
  { id: "overview", title: "Overview" },
  { id: "info-collect", title: "Information we collect" },
  { id: "plugin-disclosures", title: "Plugin-specific disclosures" },
  { id: "cookies", title: "Cookies and local storage" },
  { id: "use-info", title: "How we use information" },
  { id: "share-info", title: "When we share information" },
  { id: "overseas-disclosure", title: "Overseas disclosure" },
  { id: "retention", title: "Data retention" },
  { id: "choices", title: "Your choices" },
  { id: "complaints", title: "Privacy complaints" },
  { id: "contact", title: "Contact" },
  { id: "anti-phishing", title: "Anti-phishing note" },
];

const summaryChips = ["Account details", "Connected sites", "Optional credentials", "AI-assisted features"];

export default function Privacy() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const hash = location.hash;
  const hashId = hash ? hash.replace("#", "") : "";
  const activeId = sections.some((section) => section.id === hashId) ? hashId : "overview";

  const scrollToSection = (id: string, updateHash = true) => {
    if (typeof document === "undefined") return;
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    if (updateHash && location.hash !== `#${id}`) {
      navigate({ pathname: location.pathname, hash: `#${id}` }, { replace: false });
    }
  };

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    if (!id) return;
    const element = document.getElementById(id);
    if (!element) return;
    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [hash]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Privacy Policy | ShadowScan</title>
        <meta
          name="description"
          content="ShadowScan privacy policy covering the website, customer portal, ShadowScan Guard, and WordPress plugin data handling."
        />
      </Helmet>

      <Navigation />

      <main className="flex-grow pt-24" id="top">
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// PRIVACY_POLICY</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3">Privacy Policy</h1>
              <p className="text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto rounded-xl border border-border/60 bg-secondary/20 p-6 md:p-8 space-y-4 text-sm text-muted-foreground">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <h2 className="text-lg font-heading font-semibold text-foreground">Plain-English privacy summary</h2>
                <div className="flex flex-wrap gap-2">
                  {summaryChips.map((chip) => (
                    <span
                      key={chip}
                      className="px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-xs text-primary"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                This section summarises what data we collect and how it is used.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We collect limited technical, account, and service data needed to operate the website, portal, ShadowScan Guard, and plugin.</li>
                <li>ShadowScan Guard may store monitored site records, scan history, and site credentials you choose to provide for connected-site workflows.</li>
                <li>The WordPress plugin has narrower telemetry than ShadowScan Guard and is designed so administrator credentials are not transmitted to ShadowScan through plugin pairing or heartbeat flows.</li>
                <li>The <Link className="text-primary hover:underline" to="/plugin-addendum">ShadowScan Plugin Addendum</Link> describes plugin-specific operational terms, checkpoints, and limits.</li>
                <li>Some ShadowScan Guard features send limited prompt, scan, and site context to external AI processing infrastructure to generate advisory outputs.</li>
                <li>Analytics and error monitoring are used to operate, maintain, and improve reliability and usability.</li>
                <li>Subprocessors support specific functions of the service on our behalf; see the <Link className="text-primary hover:underline" to="/subprocessors">Subprocessors</Link> page.</li>
                <li>You can opt out of non-essential analytics and request access to or deletion of your data. Where product controls are available, you may also remove certain monitored sites or stored credentials directly.</li>
                <li>If you have a privacy concern, you can send us a complaint and we will review it. If you are not satisfied with our response, you can contact the OAIC.</li>
                <li>Questions? Contact <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
              <aside className="space-y-4">
                <div className="rounded-xl border border-border/60 bg-secondary/20 p-5 hidden lg:block sticky top-28">
                  <h3 className="text-sm font-heading font-semibold text-foreground mb-4">Sections</h3>
                  <nav>
                    <ul className="space-y-3 text-sm">
                      {sections.map((section) => {
                        const isActive = activeId === section.id;
                        return (
                          <li key={section.id}>
                            <button
                              className={`w-full text-left transition-colors ${
                                isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                              }`}
                              onClick={() => scrollToSection(section.id)}
                              type="button"
                            >
                              {section.title}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                </div>

                <div className="lg:hidden">
                  <label htmlFor="privacy-sections" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Sections
                  </label>
                  <select
                    id="privacy-sections"
                    className="mt-2 w-full rounded-lg border border-border/60 bg-secondary/30 px-4 py-3 text-sm text-foreground"
                    value={activeId}
                    onChange={(event) => scrollToSection(event.target.value)}
                  >
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.title}
                      </option>
                    ))}
                  </select>
                </div>
              </aside>

              <div className="space-y-10 text-sm text-muted-foreground leading-relaxed">
                <section id="overview" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Overview</h2>
                  <p>
                    This Privacy Policy explains how ShadowScan, operated by Valnex Holdings (“ShadowScan”, “we”, “us”), collects, uses, discloses, and protects information when you use:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>
                      Our website at <span className="font-mono">https://shadowscan.com.au</span> (the “Website”)
                    </li>
                    <li>The ShadowScan customer portal (the “Portal”)</li>
                    <li>ShadowScan Guard authenticated monitoring and Attack Surface Reduction workflows (“ShadowScan Guard”)</li>
                    <li>The ShadowScan WordPress plugin (“Plugin”), where applicable</li>
                    <li>The ShadowScan Live Scan tool (limited, external checks) (the “Live Scan”)</li>
                  </ul>
                  <p>
                    Live Scan runs only on user-initiated action, is non-intrusive, and captures limited technical metadata required to deliver the service.
                  </p>
                  <p>
                    ShadowScan Guard may store monitored site records, scan history, and optional site credentials you choose to provide so requested connected-site workflows can run. Some ShadowScan Guard features also send limited prompt or site context to external AI processing infrastructure to generate compliance, remediation, exploit-guide, and chat outputs.
                  </p>
                  <p>
                    Questions? Contact <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>.
                  </p>
                </section>

                <section id="info-collect" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Information we collect</h2>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">Information you provide</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Contact/free scan requests: name, email, website URL, optional message.</li>
                      <li>Newsletter subscriptions: email address and subscription status.</li>
                      <li>Portal and ShadowScan Guard accounts: email address and profile details you choose to provide (e.g., name, company, phone).</li>
                      <li>ShadowScan Guard monitored sites: website or site URLs and related settings you add for monitoring or connected-site workflows.</li>
                      <li>Site connection credentials you choose to provide for ShadowScan Guard, such as WordPress admin URLs, usernames, application passwords, SSH host details, usernames, ports, and SSH private keys.</li>
                      <li>AI-assisted requests: prompts, questions, and vulnerability or site context you submit through compliance, remediation, exploit-guide, or assistant features.</li>
                      <li>Domains you add: domain name and CMS type.</li>
                      <li>Checkout inputs: coupon or promotion codes you enter before checkout.</li>
                      <li>Support/feature requests: subject/title, message/description, and your email.</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">ShadowScan Guard, security, billing, and operational data</h3>
                    <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                      <li>Monitored site records, scan history, vulnerability results, and connected-site workflow status.</li>
                      <li>Results and logs related to requested remediation or protective actions.</li>
                      <li>Billing identifiers (customer/subscription IDs and invoice references). Payment card details are processed by our payment processor.</li>
                      <li>Connector status and heartbeat metadata (timestamps, version, and environment details).</li>
                      <li>Security alert records received via connected security tooling/webhooks (may include source/attacker IP if provided).</li>
                      <li>Audit logs for sensitive actions (may include timestamps and IP address metadata).</li>
                      <li>MFA and account security data (short-lived email codes; login-location checks using IP and approximate city/country).</li>
                      <li>Rate-limiting data (IP address and/or account ID plus timestamps).</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">Device/usage data (analytics)</h3>
                    <p className="text-muted-foreground">
                      The Website/Portal may include analytics and error-monitoring tools which may collect usage and device information such as page views, interactions, approximate location, browser/device details, and identifiers stored in cookies or local storage. We use analytics providers like PostHog and Matomo to understand product usage and improve the Portal experience.
                    </p>
                    <p className="text-muted-foreground">
                      Some analytics tools may record behavioral events or session activity to help diagnose issues and improve usability. You can opt out of non-essential analytics by adjusting your browser’s cookie settings or using available opt-out controls where provided.
                    </p>
                  </div>

                </section>

                <section id="plugin-disclosures" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Plugin-specific disclosures</h2>
                  <p className="text-muted-foreground">
                    The ShadowScan WordPress plugin can be used locally without pairing. When a site administrator intentionally pairs the plugin to ShadowScan, the plugin sends a narrower set of service data than ShadowScan Guard and does not submit WordPress administrator passwords or other administrator credentials to ShadowScan as part of pairing or routine heartbeat flows.
                  </p>
                  <p className="text-muted-foreground">
                    Plugin-specific operational terms, including pairing checkpoints, remote diagnostics conditions, and deactivation versus disconnect behaviour, are described in the <Link className="text-primary hover:underline" to="/plugin-addendum">ShadowScan Plugin Addendum</Link>.
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Paired plugin telemetry may include site URL, WordPress version, PHP version, connector version, control status, heartbeat timestamps, and command execution metadata.</li>
                    <li>Plugin acceptance metadata may be recorded when an authorised administrator pairs the plugin or enables remote diagnostics so ShadowScan can audit those checkpoints for the connected site.</li>
                    <li>Optional breached-password checks in the plugin use the Have I Been Pwned Passwords API. Only the first 5 hexadecimal characters of a SHA-1 password hash prefix are sent for that check, not the raw password or full hash.</li>
                    <li>Optional Sentry telemetry in the plugin is disabled by default and only sends error-event metadata after a site administrator enables it and a Sentry DSN is configured.</li>
                    <li>Optional remote diagnostics may install a temporary must-use helper only when the site administrator enables the relevant settings and the service workflow requires that troubleshooting step.</li>
                  </ul>
                  <p className="text-muted-foreground">
                    The plugin’s own documentation may provide additional service-specific detail for paired telemetry or optional diagnostics at the time a site administrator enables those features.
                  </p>
                </section>

                <section id="cookies" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Cookies and local storage</h2>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Portal and ShadowScan Guard authentication/session data is stored in browser local storage (via our authentication provider).</li>
                    <li>Portal and ShadowScan Guard UI may set functional preference cookies or local storage values.</li>
                    <li>Analytics/error-monitoring tools may set cookies or local storage identifiers.</li>
                  </ul>
                  <p className="text-muted-foreground">
                    You can control cookies and local storage through your browser settings. Blocking them may impact Portal functionality.
                  </p>
                  <p className="text-muted-foreground">
                    You can also manage analytics consent using the cookie banner.
                  </p>
                  <Button variant="link" className="px-0 text-primary" onClick={() => openAnalyticsPreferences()}>
                    Manage cookie preferences
                  </Button>
                </section>

                <section id="use-info" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">How we use information</h2>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Provide and operate the Website, Portal, ShadowScan Guard, and Plugin features.</li>
                    <li>Authenticate users and manage Portal accounts.</li>
                    <li>Store and manage monitored site records, scan history, and related ShadowScan Guard service data.</li>
                    <li>Deliver requested services (e.g., scanning, compliance, exploit-guide, assistant, and remediation workflows).</li>
                    <li>Attempt requested connected-site actions, including automated fix workflows, when you have supplied the relevant site credentials or connection details.</li>
                    <li>Generate advisory outputs for compliance, remediation, exploit-guide, and assistant features using external AI processing infrastructure where applicable.</li>
                    <li>Send operational emails (verification codes, security alerts, domain-change notifications).</li>
                    <li>Send newsletters/blog notifications if you subscribe (and honor unsubscribe requests).</li>
                    <li>Process billing and manage subscriptions (via our payment processor).</li>
                    <li>Validate coupons and affiliate referrals before checkout and apply discounts to billing.</li>
                    <li>Prevent abuse and maintain security (rate-limiting, logging, auditing).</li>
                    <li>Improve performance and usability (analytics and error monitoring).</li>
                  </ul>
                </section>

                <section id="share-info" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">When we share information</h2>
                  <p className="text-muted-foreground">
                    We may share information with service providers that help operate ShadowScan (for example: hosting, database, authentication, email delivery, payments, affiliate tracking, analytics, error monitoring, and AI-assisted processing).
                  </p>
                  <p className="text-muted-foreground">
                    Our subprocessors are listed on the <Link className="text-primary hover:underline" to="/subprocessors">Subprocessors</Link> page.
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Hosting, database, and authentication providers (Supabase)</li>
                    <li>Email delivery providers (Resend)</li>
                    <li>Payment processors (Stripe)</li>
                    <li>Affiliate attribution after consent (Rewardful)</li>
                    <li>Optional plugin error-monitoring provider (Sentry), when enabled by a site administrator</li>
                    <li>Optional plugin password-breach check provider (Have I Been Pwned Passwords API), using k-anonymity hash-prefix requests only</li>
                    <li>Third-party security tooling used for protection and monitoring</li>
                    <li>AI processing providers used to generate compliance, remediation, exploit-guide, and assistant outputs</li>
                    <li>Analytics and error-monitoring providers (PostHog, Matomo)</li>
                  </ul>
                  <p className="text-muted-foreground">
                    Where you use AI-assisted ShadowScan Guard features, the request may include prompt content, target URLs, selected standards, scan findings, or related site context needed to generate a response.
                  </p>
                  <p className="text-muted-foreground">
                    We may also share information if required by law or to protect users and ShadowScan. We do not sell personal information in the ordinary commercial sense.
                  </p>
                </section>

                <section id="overseas-disclosure" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Overseas disclosure</h2>
                  <p className="text-muted-foreground">
                    Some of the service providers we use may store or process information outside Australia, depending on the feature used and the provider’s infrastructure. This may include analytics, error monitoring, email delivery, payments, AI-assisted processing, bot protection, and optional plugin services.
                  </p>
                  <p className="text-muted-foreground">
                    Relevant providers or infrastructure may operate in countries including the United States and other countries where those providers or their sub-processors maintain systems. Because provider arrangements can change over time, the public categories on our <Link className="text-primary hover:underline" to="/subprocessors">Subprocessors</Link> page may be more general than an internal vendor inventory.
                  </p>
                  <p className="text-muted-foreground">
                    Where we disclose information overseas, we aim to take reasonable steps to ensure it is handled consistently with applicable privacy and security requirements.
                  </p>
                </section>

                <section id="retention" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Data retention</h2>
                  <p className="text-muted-foreground">
                    We keep information only as long as needed for the purposes described above, including legal, accounting, and security needs. Some records are short-lived, while account, monitored-site, alert, and audit records may be retained longer.
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Public live-scan job records, results, and generated report artifacts are normally retained for up to 7 days before automated cleanup.</li>
                    <li>ShadowScan Guard monitored site records, scan history, and related service data may remain until you remove them, the related account is closed, or we no longer need them for operational, security, or legal purposes.</li>
                    <li>Site credentials you choose to store for connected-site workflows may remain until you delete them through available product controls, the related account is closed, or we remove them during account cleanup or support handling.</li>
                    <li>AI-assisted request content and related outputs may be processed and temporarily retained by relevant service providers or our infrastructure for service delivery, troubleshooting, abuse prevention, or operational review.</li>
                    <li>Shorter-lived access tokens protect public live-scan status, report, and email endpoints and are not intended to be reusable beyond the active scan window.</li>
                    <li>Affiliate attribution only runs after analytics consent and follows the browser/device settings and retention periods of the participating services.</li>
                  </ul>
                </section>

                <section id="choices" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Your choices</h2>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Unsubscribe from newsletters via the link in emails.</li>
                    <li>Control cookies and local storage via your browser settings.</li>
                    <li>Where product controls are available, remove monitored sites or stored site credentials from ShadowScan Guard directly.</li>
                    <li>Request access/correction/deletion by emailing <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>.</li>
                  </ul>
                </section>

                <section id="complaints" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Privacy complaints</h2>
                  <p className="text-muted-foreground">
                    If you believe we have mishandled your personal information, email <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au?subject=Privacy%20Complaint">support@shadowscan.com.au</a> with the subject line “Privacy Complaint” and include enough detail for us to understand the issue.
                  </p>
                  <p className="text-muted-foreground">
                    We may ask for additional information to verify your identity or clarify the request. We aim to investigate and respond within a reasonable time.
                  </p>
                  <p className="text-muted-foreground">
                    If you are not satisfied with our response, you may also contact the Office of the Australian Information Commissioner (OAIC): <a className="text-primary hover:underline" href="https://www.oaic.gov.au/privacy/privacy-complaints" target="_blank" rel="noopener noreferrer">oaic.gov.au/privacy/privacy-complaints</a>.
                  </p>
                </section>

                <section id="contact" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Contact</h2>
                  <p className="text-muted-foreground">
                    ShadowScan, operated by Valnex Holdings - <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>
                  </p>
                </section>

                <section id="anti-phishing" className="scroll-mt-28 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Anti-phishing note</h2>
                  <p className="text-muted-foreground">
                    ShadowScan will never ask you to share your password, full payment card number, WordPress application password, SSH private key, or one-time verification codes via email, SMS, or phone. If you receive a suspicious message claiming to be ShadowScan, contact <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a> directly.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </section>
        <button
          className={`fixed bottom-6 right-6 z-50 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-secondary/80 text-foreground shadow-lg backdrop-blur transition duration-200 hover:text-primary ${
            showBackToTop ? "opacity-100 scale-100" : "pointer-events-none opacity-0 scale-95"
          }`}
          type="button"
          onClick={() => scrollToSection("top", false)}
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      </main>

      <Footer />
    </div>
  );
}
